import React, { useState, useEffect } from "react";
 import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import frameBg from "../../../../Assets/FrameBGDashboard.png";
import NotificationAlert from "../../../Alerts/NotificationAlert";
import { createProject } from "../../AdminServices/AdminProjectService";
import { getProjectTypes } from "../../AdminServices/AdminMetadataService";

const CreateProjectModal = ({ show, onClose, onCreated }) => {
  const initialForm = {
    projectName: "",
    projectDescription: "",
    projectType: "",
  };

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [projectTypes, setProjectTypes] = useState([]);
   const navigate = useNavigate();

  // Reset form each time modal opens
  useEffect(() => {
    if (show) {
      setForm(initialForm);
      setErrors({});
      setAlert(null);
      loadMetaData();
    }
  }, [show]);

  const loadMetaData = async () => {
    try {
      const [typeRes] = await Promise.all([getProjectTypes()]);
      setProjectTypes(typeRes.data?.data || []);
    } catch (err) {
      if (err.response?.status === 400) {
      navigate("/bad-request", {
        state: {
          error: err.response?.data?.message || "Bad request (400)",
        },
      });
      return;
    }
      console.error("Error loading metadata", err);
    }
  };

  if (!show) return null;

  // Handle input change
  const handleInput = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Validation
  const validate = () => {
    let temp = {};

    if (!form.projectName.trim())
      temp.projectName = "Project name is required.";

    if (!form.projectDescription.trim())
      temp.projectDescription = "Project description is required.";

    if (!form.projectType || form.projectType === "")
      temp.projectType = "Project type is required.";

    return temp;
  };

  // Submit form
  const handleSubmit = async () => {
    const validation = validate();

    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const res = await createProject(form);

      if (res.data.status === "Success") {
        setAlert({
          type: "success",
          message: res.data.message || "Success!",
        });
        if (onCreated) onCreated();

        setTimeout(() => {
          setAlert(null);
          onClose();
        }, 1000);
      } else {
        setAlert({ type: "error", message: res.data.message });
      }
    } catch (err) {
       if (err.response?.status === 400) {
      navigate("/bad-request", {
        state: {
          error: err.response?.data?.message || "Bad request (400)",
        },
      });
      return;
    }
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Something went wrong.",
      });
    }

    setLoading(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex justify-end z-50"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-4xl h-full shadow-2xl border-l border-gray-200 flex flex-col animate-slideIn overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex justify-between items-center px-8 py-6 border-b text-white shadow-md bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${frameBg})` }}
        >
          <h2 className="text-2xl font-semibold">Create New Project</h2>
          <button onClick={onClose}>
            <X size={26} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Project Name */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="projectName"
                value={form.projectName}
                onChange={handleInput}
                className={`w-full p-2 border rounded-md mt-1 text-sm ${
                  errors.projectName ? "border-red-500" : ""
                }`}
                placeholder="Enter project name"
              />
              {errors.projectName && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.projectName}
                </p>
              )}
            </div>

            {/* Project Description */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="projectDescription"
                rows={4}
                value={form.projectDescription}
                onChange={handleInput}
                className={`w-full p-2 border rounded-md mt-1 text-sm ${
                  errors.projectDescription ? "border-red-500" : ""
                }`}
                placeholder="Enter project description"
              />
              {errors.projectDescription && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.projectDescription}
                </p>
              )}
            </div>

            {/* Project Type */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Project Type <span className="text-red-500">*</span>
              </label>
              <select
                name="projectType"
                value={form.projectType}
                onChange={handleInput}
                className={`w-full p-2 border rounded-md mt-1 text-sm ${
                  errors.projectType ? "border-red-500" : ""
                }`}
              >
                <option value="">Select Project Type</option>
                {projectTypes.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              {errors.projectType && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.projectType}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t bg-white flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm border rounded-md text-gray-600 hover:bg-gray-100"
            disabled={loading}
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            className="px-6 py-2 text-sm bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white rounded-md shadow hover:opacity-90"
            disabled={loading}
          >
            {loading ? "Creating..." : "Create Project"}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {alert && (
        <NotificationAlert
          type={alert.type}
          message={alert.message}
          show={true}
          duration={4000}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default CreateProjectModal;
